import { createSnapshotValue as createMutable } from '../../lib/snapshot-value.ts'
import type { Tone } from '../components/primitives.tsx'

/**
 * Spec §12.8: an outbound shipment is its own record, owned by logistics. It never touches the
 * stock ledger. One order can leave in several shipments (a client may ask for two batches).
 */
export type Carrier = 'nova_poshta' | 'own' | 'pickup' | 'other'

export const CARRIER_LABELS: Record<Carrier, string> = {
  nova_poshta: 'Нова пошта',
  own: 'Власний транспорт',
  pickup: 'Самовивіз',
  other: 'Інший перевізник',
}

export type ShipmentStatus = 'planned' | 'in_transit' | 'at_branch' | 'delivered'

export const SHIPMENT_STATUS: Record<ShipmentStatus, { label: string; tone: Tone }> = {
  planned: { label: 'Готується', tone: 'neutral' },
  in_transit: { label: 'В дорозі', tone: 'active' },
  at_branch: { label: 'У відділенні', tone: 'warning' },
  delivered: { label: 'Доставлено', tone: 'done' },
}

export const HOME_COUNTRY = 'Україна'

/** Suggestions only: any country can be typed. */
export const COUNTRIES = [
  'Україна',
  'Молдова',
  'Польща',
  'Румунія',
  'Словаччина',
  'Угорщина',
  'Чехія',
  'Литва',
  'Німеччина',
]

export const isExport = (shipment: { destination: { country: string } }) =>
  shipment.destination.country !== HOME_COUNTRY

/** What the carrier's document is called: ТТН at home, CMR on a truck abroad. */
export const documentLabel = (shipment: { carrier: Carrier; destination: { country: string } }) =>
  shipment.carrier === 'nova_poshta'
    ? isExport(shipment)
      ? 'Номер Nova Post'
      : 'ТТН'
    : isExport(shipment)
      ? 'CMR'
      : 'Номер ТТН'

export interface TrackingEvent {
  at: string
  text: string
  /** Where the status came from: Nova Poshta tracking or a person. */
  source: 'nova_poshta' | 'manual'
}

export interface ShipmentLine {
  lineId: string
  quantity: string
}

export interface ShipmentCargo {
  name: string
  quantity: string
  unit: string
}

export interface ProtoShipmentRecord {
  id: string
  number: string
  dealId: string | null
  lines: ShipmentLine[]
  cargo?: ShipmentCargo[]
  carrier: Carrier
  /** Only for «Інший перевізник». */
  carrierName: string | null
  /** A snapshot: the client's contact may change role or leave later. */
  recipient: { name: string; phone: string | null; role: string | null }
  /** City plus an address, or a Nova Poshta branch. */
  destination: { country: string; city: string; point: string }
  /** Nova Poshta ТТН (14 digits), a Nova Post number abroad, or the ТТН/CMR of other carriers. */
  ttn: string | null
  /** Export only: the customs declaration number. The paperwork itself stays outside the CRM. */
  customsDeclaration: string | null
  /** Own transport: driver and vehicle, as the logistician writes them. */
  vehicle: string | null
  places: number | null
  weightKg: string | null
  plannedAt: string
  shippedAt: string | null
  eta: string | null
  deliveredAt: string | null
  status: ShipmentStatus
  events: TrackingEvent[]
  trackedAt: string | null
  createdById: string
  createdAt: string
}

export const SHIPMENTS: ProtoShipmentRecord[] = createMutable([] as ProtoShipmentRecord[])
